/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import Interstitial from "@atg/icf-react-components/components/Interstitial";
import { InterstitialColors } from "@atg/icf-react-components/components/Interstitial/Interstitial";
import { SvgIconName } from "@atg/icf-react-components/components/SvgIconsMap";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RequestStatus } from "~/constants";
import { selectTwoFactorAuthTokenValidationStatus } from "~/features/auth/selectors";

export enum TwoFactorInterstitialType {
  success = "success",
  error = "error",
  differentSession = "differentSession",
}

interface Props {
  type: TwoFactorInterstitialType;
  setIsOpen: (isOpen: boolean) => void;
  children: ReactNode;
}

const TwoFactorAuthInterstitialWrapper: FC<Props> = ({ type, setIsOpen, children }) => {
  const { t } = useTranslation("auth");
  const tokenValidationStatus = useSelector(selectTwoFactorAuthTokenValidationStatus);
  const navigate = useNavigate();

  const matrix = {
    [TwoFactorInterstitialType.success]: {
      title: t("two_factor_auth.modal.text.success"),
      color: "success",
      icon: "check",
      openStatus: RequestStatus.SUCCESS,
      action: () => setIsOpen(false),
      autoHideDuration: 2000,
    },
    [TwoFactorInterstitialType.error]: {
      title: t("two_factor_auth.validate_token.error"),
      color: "error",
      icon: "error",
      openStatus: RequestStatus.ERROR,
      action: () => {
        setIsOpen(false);
        void navigate("/app/dashboard", { replace: true });
      },
      autoHideDuration: 2000,
    },
    [TwoFactorInterstitialType.differentSession]: {
      title: t("two_factor_auth.modal.text.different_session"),
      color: "info",
      icon: "info",
      openStatus: RequestStatus.ERROR,
      autoHideDuration: 4000,
      action: () => {
        setIsOpen(false);
        void navigate("/app/dashboard", { replace: true });
      },
    },
  };

  const config = matrix[type as keyof typeof matrix];

  return (
    <>
      <Interstitial
        title={config.title}
        iconName={config.icon as SvgIconName}
        color={config.color as InterstitialColors}
        open={tokenValidationStatus === config.openStatus}
        onClose={() => config.action()}
        autoHideDuration={config.autoHideDuration}
      />
      {children}
    </>
  );
};

export default TwoFactorAuthInterstitialWrapper;
